<template>
  <section class="tech-panel environment-panel">
    <div class="panel-title">生长环境综合研判</div>
    <div class="metric-row">
      <div class="metric-item" v-for="item in metrics" :key="item.name" :class="{ yellow: item.yellow }">
        <span><i></i>{{ item.name }}</span>
        <strong>{{ item.value }}</strong>
      </div>
    </div>
    <div class="environment-charts">
      <div ref="radarRef" class="chart-box"></div>
      <div ref="lineRef" class="chart-box"></div>
    </div>
  </section>
</template>

<script setup>
import * as echarts from "echarts";
import { onBeforeUnmount, onMounted, ref } from "vue";

const radarRef = ref(null);
const lineRef = ref(null);
let radarChart = null;
let lineChart = null;

const metrics = [
  { name: "长势指数", value: "86.2" },
  { name: "墒情指数", value: "72.5" },
  { name: "养分指数", value: "81.3", yellow: true },
  { name: "胁迫指数", value: "18.7" },
];

const stressValues = [24, 31, 28, 46, 38, 27];

function getRecentDateLabels(stepDays = 2, count = 6) {
  const today = new Date();
  return Array.from({ length: count }, (_, index) => {
    const date = new Date(today);
    date.setDate(today.getDate() - (count - 1 - index) * stepDays);
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${month}/${day}`;
  });
}

function resizeCharts() {
  radarChart && radarChart.resize();
  lineChart && lineChart.resize();
}

function initRadar() {
  radarChart = echarts.init(radarRef.value);
  radarChart.setOption({
    color: ["#37fff0", "#ffe264"],
    legend: {
      bottom: 0,
      left: "center",
      itemWidth: 24,
      itemHeight: 10,
      itemGap: 28,
      textStyle: {
        color: "#f2fffd",
        fontSize: 22,
        fontWeight: "bold",
      },
      data: ["当前指数", "适宜阈值"],
    },
    radar: {
      center: ["50%", "47%"],
      radius: "62%",
      startAngle: 90,
      splitNumber: 3,
      shape: "polygon",
      axisName: {
        color: "#f2fffd",
        fontSize: 22,
        fontWeight: "bold",
      },
      axisLine: { lineStyle: { color: "rgba(86, 255, 238, 0.28)" } },
      splitLine: { lineStyle: { color: "rgba(86, 255, 238, 0.32)" } },
      splitArea: { areaStyle: { color: ["rgba(16, 105, 95, 0.08)", "rgba(16, 105, 95, 0.02)"] } },
      indicator: [
        { name: "生长指数", max: 100 },
        { name: "虫害安全", max: 100 },
        { name: "抗逆指数", max: 100 },
        { name: "养分指数", max: 100 },
        { name: "墒情指数", max: 100 },
        { name: "气象适宜", max: 100 },
      ],
    },
    series: [
      {
        type: "radar",
        symbol: "none",
        lineStyle: { width: 3 },
        areaStyle: { opacity: 0.34 },
        data: [
          {
            name: "当前指数",
            value: [86, 78, 76, 81, 73, 75],
            areaStyle: { color: "rgba(53, 255, 235, 0.36)" },
            lineStyle: { color: "#33fff0" },
          },
          {
            name: "适宜阈值",
            value: [75, 70, 70, 75, 70, 70],
            areaStyle: { color: "rgba(255, 226, 100, 0.18)" },
            lineStyle: { color: "#ffe264" },
          },
        ],
      },
    ],
  });
}

function initLine() {
  lineChart = echarts.init(lineRef.value);
  lineChart.setOption({
    title: {
      text: "环境胁迫指数",
      left: "center",
      top: 0,
      textStyle: {
        color: "#f2fffd",
        fontSize: 30,
        fontWeight: "bold",
      },
    },
    grid: { top: 52, right: 22, bottom: 46, left: 38 },
    tooltip: {
      trigger: "axis",
      backgroundColor: "rgba(4, 38, 39, 0.92)",
      borderColor: "rgba(82, 255, 241, 0.48)",
      textStyle: { color: "#fff", fontSize: 22 },
      formatter: (params) => {
        const item = params[0];
        return `${item.axisValue}<br/>环境胁迫指数：${item.value}`;
      },
    },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: getRecentDateLabels(),
      axisTick: { show: false },
      axisLine: { show: false },
      axisLabel: { color: "#ffffff", fontSize: 22, fontWeight: "bold", margin: 7 },
      splitLine: { show: true, lineStyle: { color: "rgba(83,255,237,.13)", type: "dashed" } },
    },
    yAxis: {
      type: "value",
      min: 0,
      max: 100,
      splitNumber: 5,
      axisLabel: { show: false },
      axisTick: { show: false },
      axisLine: { show: false },
      splitLine: { lineStyle: { color: "rgba(83,255,237,.13)", type: "dashed" } },
    },
    series: [
      {
        name: "环境胁迫指数",
        type: "line",
        smooth: false,
        symbol: "circle",
        symbolSize: 13,
        data: stressValues,
        lineStyle: { width: 5, color: "#5ffff2" },
        itemStyle: { color: "#dfffff", borderColor: "#5ffff2", borderWidth: 3 },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: "rgba(37, 233, 195, 0.64)" },
            { offset: 1, color: "rgba(37, 233, 195, 0.2)" },
          ]),
        },
      },
    ],
  });
}

onMounted(() => {
  initRadar();
  initLine();
  window.addEventListener("resize", resizeCharts);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", resizeCharts);
  radarChart && radarChart.dispose();
  lineChart && lineChart.dispose();
});
</script>

<style lang="less" scoped>
@import "./guidancePanelShared.less";

.environment-panel {
  padding: 0 42px 0;
  display: flex;
  flex-direction: column;
}

.environment-panel .panel-title {
  width: 540px;
}

.metric-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 34px;
  margin-top: 50px;
}

.metric-item {
  position: relative;
  min-width: 0;
  padding-left: 18px;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.metric-item i {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #66fff1;
  box-shadow: 0 0 10px rgba(102, 255, 241, 0.86);
}

.metric-item span {
  color: #dffdfb;
  font-size: 30px;
  font-style: normal;
  line-height: 26px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.metric-item strong {
  display: inline-block;
  margin: 6px 8px 0 16px;
  color: #75fff4;
  font-size: 60px;
  line-height: 50px;
}

.metric-item.yellow i {
  background: #ffe66f;
  box-shadow: 0 0 10px rgba(255, 230, 111, 0.78);
}

.metric-item.yellow strong {
  color: #ffe66f;
}

.environment-charts {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 38px;
  align-items: center;
}

.chart-box {
  width: 100%;
  height: 92%;
  min-height: 0;
}
</style>
